import { Stack } from '@mui/material';
import { RefObject, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Footer } from '@/components/navigation/Footer';
import { useFormContext } from '@/components/react-hook-form';
import { AIUserFeedbackButton } from '@/modules/aiUserFeedback/AIUserFeedbackButton';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { ToggleChatButton } from '@/modules/documents/EditSummaryTrowser/components/ToggleChatButton';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntitySection,
  isBDGSection,
  isDPSection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getAdjacentSectionOption } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { useHouseholdChatReplacement } from '@/modules/household/chat/hooks/useHouseholdChatReplacement';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { UserFeedbackFeedbackCategory } from '@/types/schema';

function useCanEditCurrentSection() {
  const canEditHousehold = useCanEditHouseholdData();
  const canEditDP = useCanEditDispositiveProvisions();
  const { currentSection } = useEditEntitySplitScreenModalContext();
  return isDPSection(currentSection) || isBDGSection(currentSection)
    ? canEditDP
    : canEditHousehold;
}

export function EditEntitySplitScreenFooter() {
  const canEdit = useCanEditCurrentSection();
  const { footerRef, isDPSection, handleCloseModal } =
    useEditEntitySplitScreenModalContext();

  const chatContext = useOptionalDocumentChatContext();

  const isAIEnabled = useAICapabilitiesEnabled();
  const { isHouseholdChatReplacementEnabled } = useHouseholdChatReplacement();
  const isDocumentChatEnabled =
    !!chatContext && isAIEnabled && !isHouseholdChatReplacementEnabled;

  const feedbackCategory = useMemo(
    () =>
      isDPSection
        ? UserFeedbackFeedbackCategory.AiDpSuggestions
        : UserFeedbackFeedbackCategory.EntityFormAiSuggestions,
    [isDPSection]
  );

  return (
    <Footer
      leftAction={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => handleCloseModal(true)}
          >
            {canEdit ? 'Exit without saving' : 'Close'}
          </Button>
          {isDocumentChatEnabled && (
            <ToggleChatButton
              isChatOpen={chatContext.isChatOpen}
              onOpenChat={chatContext.openChat}
              onCloseChat={chatContext.closeChat}
            />
          )}
          {canEdit && (
            <AIUserFeedbackButton feedbackCategory={feedbackCategory} />
          )}
        </Stack>
      }
      // The close/save buttons will get portaled here, as children of the stack.
      rightAction={
        <Stack
          ref={footerRef}
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
        />
      }
    />
  );
}

interface EditEntitySplitScreenFooterRightActionsProps {
  onSubmit: (afterSubmitSection?: EditEntitySection) => void;
}

/**
 * The right actions for the footer of the EditEntitySplitScreen modal.
 * This component is portaled because it requires form context.
 *
 * @param onSubmit
 * @constructor
 */
export function EditEntitySplitScreenFooterRightActions({
  onSubmit,
}: EditEntitySplitScreenFooterRightActionsProps) {
  const canEdit = useCanEditCurrentSection();
  const {
    formState: { isSubmitting },
  } = useFormContext<CombinedSupportedSubformFieldTypes>();

  const { clickedSubmitButton, setClickedSubmitButton } =
    useEditEntitySplitScreenModalContext();

  const handleButtonClick = useCallback(
    (
      button: 'save' | 'prev' | 'next',
      afterSubmitSection?: EditEntitySection
    ) => {
      setClickedSubmitButton(button);
      onSubmit(afterSubmitSection);
    },
    [onSubmit, setClickedSubmitButton]
  );

  return (
    <>
      <Stack direction="row" spacing={1}>
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            type="submit"
            onClick={() => handleButtonClick('save')}
            loading={isSubmitting && clickedSubmitButton === 'save'}
            disabled={isSubmitting}
          >
            Save & close
          </Button>
        )}
        <Stack direction="row" spacing={0.5}>
          <NextPrevEditEntitySectionFormButton
            onRequestSubmit={(dir, afterSubmitSection) =>
              handleButtonClick(dir, afterSubmitSection)
            }
            direction="prev"
          />
          <NextPrevEditEntitySectionFormButton
            onRequestSubmit={(dir, afterSubmitSection) =>
              handleButtonClick(dir, afterSubmitSection)
            }
            direction="next"
          />
        </Stack>
      </Stack>
    </>
  );
}

interface NextPrevEditEntitySectionFormButtonProps {
  onRequestSubmit: (
    dir: 'next' | 'prev',
    afterSubmitSection: EditEntitySection
  ) => void;
  direction: 'next' | 'prev';
}

function NextPrevEditEntitySectionFormButton({
  onRequestSubmit,
  direction,
}: NextPrevEditEntitySectionFormButtonProps) {
  const {
    formState: { isSubmitting },
  } = useFormContext<CombinedSupportedSubformFieldTypes>();

  const { currentSection, sectionOptions, clickedSubmitButton } =
    useEditEntitySplitScreenModalContext();

  // Determine the next or previous section
  const targetSection = useMemo(
    () => getAdjacentSectionOption(direction, currentSection, sectionOptions),
    [currentSection, direction, sectionOptions]
  );

  return (
    <IconButton
      variant="secondary"
      size="sm"
      type="button"
      onClick={() => onRequestSubmit(direction, targetSection)}
      loading={isSubmitting && clickedSubmitButton === direction}
      disabled={isSubmitting}
      icon={direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon}
      ariaLabel={direction === 'prev' ? 'Previous' : 'Next'}
    />
  );
}

interface EditEntitySplitScreenFooterActionsPlaceholderProps {
  footerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Disabled stand-in for {@link EditEntitySplitScreenFooterRightActions},
 * portaled into the footer while the right-pane form loads. The real actions
 * are portaled from the form, so they vanish while a newly selected target
 * loads; this keeps them visibly present (greyed) and reserves their footprint
 * so nothing shifts when the live buttons mount. Mirrors the live layout.
 */
export function EditEntitySplitScreenFooterActionsPlaceholder({
  footerRef,
}: EditEntitySplitScreenFooterActionsPlaceholderProps) {
  const canEdit = useCanEditCurrentSection();

  if (!footerRef.current) return null;

  return createPortal(
    <Stack direction="row" spacing={1}>
      {canEdit && (
        <Button variant="primary" size="sm" type="button" disabled>
          Save & close
        </Button>
      )}
      <Stack direction="row" spacing={0.5}>
        <IconButton
          variant="secondary"
          size="sm"
          type="button"
          disabled
          icon={ChevronLeftIcon}
          ariaLabel="Previous"
        />
        <IconButton
          variant="secondary"
          size="sm"
          type="button"
          disabled
          icon={ChevronRightIcon}
          ariaLabel="Next"
        />
      </Stack>
    </Stack>,
    footerRef.current
  );
}
